import {
  AddCircleOutlined,
  AltRoute,
  ArrowForward,
  CancelScheduleSend,
  CheckCircleOutlined,
  DynamicFeedOutlined,
  EditOutlined,
  ErrorOutlineOutlined,
  FastForward,
  Flag,
  GpsOff,
  HourglassEmpty,
  Inventory2Outlined,
  LocalShippingOutlined,
  LocationOff,
  ManageSearch,
  PersonAddAlt,
  PersonOff,
  PersonRemoveOutlined,
  PlaceOutlined,
  PlayArrow,
  ReportProblemOutlined,
  Route,
  SatelliteAlt,
  Schedule,
  SendOutlined,
  SwapVert,
  Sync,
  SyncAlt,
  TrendingUp,
  VpnKeyOutlined,
  WarningAmber,
  type SvgIconComponent,
} from "@mui/icons-material";

import {
  BoardingStateEnum,
  DEFAULT_ROUTE_COLOR_OBJECT,
  MapConst,
  RideInvestigationEventTypeEnum,
  RideInvestigationHistoryEntryTypeEnum,
  RideInvestigationHistorySourceEnum,
  RideInvestigationSeverityEnum,
  RideStatusEnum,
  type RydeUiUnit,
} from "@ryde/shared";
import {
  blueMui,
  greenMui,
  greyMui,
  orangeMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

import type {
  ILegendRow,
  TInvestigationDriveRowKind,
  TInvestigationHistoryFilter,
  TInvestigationHistoryGroup,
  TInvestigationHistoryIconKey,
  TInvestigationLifecycleStepKey,
} from "./investigation.models";

/** Wall-clock milliseconds a whole ride takes to play at 1× (playback compresses long rides to a watchable length). */
export const PLAYBACK_BASE_WALL_MS = 40_000;

/** Selectable playback rate multipliers. */
export const PLAYBACK_SPEED_OPTIONS = [0.5, 1, 2, 4] as const;

export const PLAYBACK_DEFAULT_SPEED = 1;

/** How often (ms) throttled readouts (timestamp, occupancy, current station) refresh while playing. */
export const SNAPSHOT_THROTTLE_MS = 120;

/** How long (ms) a "just happened" event callout stays visible during playback. */
export const JUST_FIRED_EVENT_VISIBLE_MS = 3_500;

/** Station popup sits this far (px) above the pin's tip. */
export const STATION_POPUP_OFFSET_PX = 20;

/**
 * Cap (px) on the anomaly list inside its banner — roughly six rows, then it scrolls. Without a cap a ride
 * with dozens of anomalies pushes the panel tabs and their content off the bottom.
 */
export const ANOMALY_LIST_MAX_HEIGHT_PX = 160;

/** Playback vehicle marker footprint (px). */
export const INVESTIGATION_MARKER_SIZE = 26;

/** Right info-panel width (px) and its resize bounds for the draggable split. */
export const INVESTIGATION_PANEL_WIDTH = 500;
export const INVESTIGATION_PANEL_MIN_WIDTH = 320;
export const INVESTIGATION_PANEL_MAX_WIDTH = 680;

/**
 * Opening width (px) once Ask AI joins the tab bar. Six full-width tabs at 500px are ~83px each, which
 * is narrower than "Passengers" needs and wraps the row; this gives every tab its label on one line
 * from the first frame, without the user reaching for the resizer.
 */
export const INVESTIGATION_PANEL_WIDTH_WITH_AI = 660;

/** Passenger roster row height (px) — an accessible target for the whole clickable row. */
export const PASSENGER_ROW_MIN_HEIGHT_PX = 44;

/** Panel tabs sizing (px). */
export const PANEL_TAB_MIN_HEIGHT_PX = 42;
export const PANEL_TAB_FONT_SIZE_PX = 13;
export const PANEL_TAB_PADDING_INLINE_PX = 8;

/**
 * The Ask-AI tab's sparkle: sized to sit on the label's line rather than tower over it, and kept in the
 * advisor's own violet whether the tab is selected or not, so the one AI surface in the panel is marked
 * as such at a glance. Same accent the advisor drawer's send button and chips already use.
 */
export const PANEL_TAB_ICON_SX = {
  fontSize: 18,
  color: rydeColors.venetianNights,
} as const;

/** MUI `sx` for the panel Tabs container and each Tab (kept out of the component file). */
export const TABS_SX = { minHeight: PANEL_TAB_MIN_HEIGHT_PX } as const;
export const TAB_SX = {
  minHeight: PANEL_TAB_MIN_HEIGHT_PX,
  minWidth: 0,
  // MUI's 16px side padding costs 32px per tab, which is what pushes six of them into wrapping once
  // the panel is dragged back down towards its minimum.
  paddingInline: `${PANEL_TAB_PADDING_INLINE_PX}px`,
  textTransform: "none",
  fontSize: PANEL_TAB_FONT_SIZE_PX,
} as const;

/** Time math. */
export const MS_PER_SECOND = 1_000;
export const MS_PER_MINUTE = 60_000;
export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 3_600;
export const DECIMAL_BASE = 10;
export const COORDS_PER_POINT = 2;
export const DEFAULT_ROUND_DECIMALS = 1;
export const KM_ROUND_DECIMALS = 2;
export const TIME_PAD_LENGTH = 2;

/** Divisor for the midpoint index in the playback binary searches. */
export const MIDPOINT_DIVISOR = 2;

/** Multiplier turning a 0..1 fraction into a CSS percentage. */
export const PERCENT = 100;

/** CSS class on the rotating inner span of the playback marker, and its query selector. */
export const MARKER_ROTATE_CLASS = "rideinv-marker-rot";
export const MARKER_ROTATE_SELECTOR = `.${MARKER_ROTATE_CLASS}`;

/** Decimal places kept for generated SVG path coordinates. */
export const SVG_COORD_DECIMALS = 2;

/** Occupancy sparkline geometry (px): top inset and plot height. */
export const CHART_TOP = 2;
export const CHART_PLOT_HEIGHT = 36;
export const SPARK_STROKE_WIDTH = 1.5;
/** Sparkline SVG viewBox (width = PERCENT so path x-coords map 1:1). */
export const SPARK_VIEWBOX = "0 0 100 40";

/** A time gap larger than this between consecutive GPS points is drawn as a "no signal" segment. */
export const GPS_GAP_THRESHOLD_MS = 180_000;

/** Trail layer colors: teal planned route, distinct blue actual GPS, grey signal gaps, red detour. */
export const TRAIL_ROUTE_COLOR = DEFAULT_ROUTE_COLOR_OBJECT.colorValue;
export const TRAIL_ACTUAL_COLOR = blueMui._700;
export const TRAIL_NO_GPS_COLOR = MapConst.DASHED_LINE_COLOR;
export const TRAIL_DETOUR_COLOR = MapConst.GPS_TRAIL_DETOUR_COLOR;
export const TRAIL_POINT_COLOR = blueMui._800;
export const STATION_COLOR = DEFAULT_ROUTE_COLOR_OBJECT.colorValue;

/** Trail legend rows (planned route, real GPS, no-signal gap, detour) rendered by the map legend. */
export const LEGEND_ROWS: ILegendRow[] = [
  {
    labelKey: "rideMap.LEGEND_PLANNED_ROUTE",
    color: TRAIL_ROUTE_COLOR,
    dash: "solid",
  },
  {
    labelKey: "rideMap.LEGEND_REAL_GPS",
    color: TRAIL_ACTUAL_COLOR,
    dash: "dashed",
  },
  {
    labelKey: "rideMap.LEGEND_NO_SIGNAL",
    color: TRAIL_NO_GPS_COLOR,
    dash: "dotted",
  },
  {
    labelKey: "rideMap.LEGEND_DETOUR",
    color: TRAIL_DETOUR_COLOR,
    dash: "dashed",
  },
];

/**
 * Trail line rendering (weights / opacities / dash patterns). Here it is the PLANNED route that is shifted
 * a few screen pixels sideways, so the trail keeps its true path — the reverse of the live map, where the
 * trail moves and the planned route stays put.
 */
export const PLANNED_LINE_WEIGHT = 5;
export const ACTUAL_LINE_WEIGHT = 4;
export const GAP_LINE_WEIGHT = 3.5;
export const PLANNED_LINE_OPACITY = 0.9;
export const ACTUAL_LINE_OPACITY = 1;
export const GAP_LINE_OPACITY = 1;
/** Screen-pixel offset of the GPS trail from the planned route (kept constant on-screen across zooms). */
export const TRAIL_OFFSET_PX = 12;
export const ACTUAL_DASH_ARRAY = "10 8";
export const DETOUR_DASH_ARRAY = "9 6";
export const GAP_DASH_ARRAY = "1 9";

/** Shared source/layer ids for the MapLibre trail-dot layer (one layer for every sample dot). */
export const INVESTIGATION_DOTS_SOURCE_ID = "investigation-trail-dots";
export const INVESTIGATION_DOTS_LAYER_ID = "investigation-trail-dots-layer";

export const TRAIL_DOT_RADIUS = 3.2;
export const TRAIL_DOT_BORDER_WEIGHT = 1.5;
export const MAX_TRAIL_DOTS = 40;

/** Max decimated GPS samples listed in the Tracing tab. */
export const MAX_TRACING_ROWS = 200;

/**
 * Follow-the-vehicle camera. The vehicle may roam the middle of the viewport freely; the camera only
 * re-centres once it crosses into the outer `FOLLOW_SAFE_INSET_RATIO` of either axis, so a ride that
 * stays on screen is never nudged. Zoom is left alone — following must not undo the user's own framing.
 */
export const FOLLOW_SAFE_INSET_RATIO = 0.25;

/** Glide (ms) used to bring the vehicle back to the centre. */
export const FOLLOW_EASE_MS = 700;

/** Minimum wall-clock gap (ms) between two follow checks — the playhead itself ticks every frame. */
export const FOLLOW_CHECK_INTERVAL_MS = 150;

/** Map fit-to-ride padding (px) and max zoom. */
export const MAP_FIT_PADDING_PX = 44;
export const MAP_FIT_MAX_ZOOM = 16;

/** Top-bar filter swatch sizing (px) and inactive opacity. */
export const SWATCH_LINE_WIDTH_PX = 16;
export const SWATCH_LINE_BORDER_PX = 3;
export const SWATCH_DOT_SIZE_PX = 10;
export const SWATCH_INACTIVE_OPACITY = 0.45;

/** Timeline tick dimensions (px): station ticks (thin bars) and event ticks (dots), base vs active. */
export const TIMELINE_STATION_TICK_WIDTH = 3;
export const TIMELINE_STATION_TICK_ACTIVE_WIDTH = 5;
export const TIMELINE_STATION_TICK_HEIGHT = 18;
export const TIMELINE_STATION_TICK_ACTIVE_HEIGHT = 24;
export const TIMELINE_EVENT_TICK_SIZE = 11;
export const TIMELINE_EVENT_TICK_ACTIVE_SIZE = 15;

/** MUI icon per event type — used by the timeline and the event feed. */
export const RIDE_INVESTIGATION_EVENT_ICON: Record<
  RideInvestigationEventTypeEnum,
  SvgIconComponent
> = {
  [RideInvestigationEventTypeEnum.RideStarted]: PlayArrow,
  [RideInvestigationEventTypeEnum.ArrivedAtStation]: PlaceOutlined,
  [RideInvestigationEventTypeEnum.StoppedAtStation]: HourglassEmpty,
  [RideInvestigationEventTypeEnum.DepartedStation]: ArrowForward,
  [RideInvestigationEventTypeEnum.PassengerBoarded]: PersonAddAlt,
  [RideInvestigationEventTypeEnum.PassengerNoShow]: PersonOff,
  [RideInvestigationEventTypeEnum.DetourEntered]: AltRoute,
  [RideInvestigationEventTypeEnum.DetourExited]: Route,
  [RideInvestigationEventTypeEnum.GpsGap]: GpsOff,
  [RideInvestigationEventTypeEnum.UnexpectedStop]: WarningAmber,
  [RideInvestigationEventTypeEnum.Incident]: ReportProblemOutlined,
  [RideInvestigationEventTypeEnum.RideEnded]: Flag,
};

/** Semantic color per severity (event/anomaly chips, timeline ticks). */
export const RIDE_INVESTIGATION_SEVERITY_COLOR: Record<
  RideInvestigationSeverityEnum,
  string
> = {
  [RideInvestigationSeverityEnum.Info]: blueMui._500,
  [RideInvestigationSeverityEnum.Warning]: orangeMui._600,
  [RideInvestigationSeverityEnum.Critical]: redMui._500,
};

/** Matching soft background per severity (existing theme tints — never alpha-hex concatenation). */
export const RIDE_INVESTIGATION_SEVERITY_BG: Record<
  RideInvestigationSeverityEnum,
  string
> = {
  [RideInvestigationSeverityEnum.Info]: blueMui._50,
  [RideInvestigationSeverityEnum.Warning]: orangeMui._50,
  [RideInvestigationSeverityEnum.Critical]: redMui._50,
};

/** Boarding-status chip colors (text + soft fill), so the roster never reads by color alone. */
export const PASSENGER_STATUS_COLOR: Record<BoardingStateEnum, string> = {
  [BoardingStateEnum.Boarded]: greenMui._700,
  [BoardingStateEnum.NotBoarded]: redMui._700,
  [BoardingStateEnum.Unknown]: greyMui._700,
};

export const PASSENGER_STATUS_BG: Record<BoardingStateEnum, string> = {
  [BoardingStateEnum.Boarded]: greenMui._50,
  [BoardingStateEnum.NotBoarded]: redMui._50,
  [BoardingStateEnum.Unknown]: greyMui._100,
};

/** Neutral active-chip / delay-chip fill (existing theme shade). */
export const INVESTIGATION_ACTIVE_CHIP_BG = greyMui._100;

/** Neutral tone for muted labels / dividers within the panel. */
export const INVESTIGATION_MUTED_COLOR = greyMui._600;

/** Entity the Ask-AI tab speaks for: the advisor is asked about this one ride, by id. */
export const INVESTIGATION_AI_ENTITY: RydeUiUnit = "ride";

/** Starter questions offered on an empty Ask-AI transcript (i18n keys under `askAi.suggestions`). */
export const INVESTIGATION_AI_SUGGESTION_KEYS = [
  "summary",
  "late",
  "noShow",
  "detour",
] as const;

/** Ask-AI transcript box: the height it holds even while the conversation is empty. */
export const ASK_AI_TRANSCRIPT_MIN_HEIGHT_PX = 220;

/** Id tying the Ask-AI composer's visible label to its textarea. */
export const ASK_AI_INPUT_ID = "ride-investigation-ask-ai-input";

/** Shortest question the Ask-AI send button accepts. */
export const ASK_AI_MIN_CHARS = 2;

/** Rows the Ask-AI input grows to before it scrolls. */
export const ASK_AI_INPUT_ROWS = 3;

/** data-e2e keys for non-styled elements (MUI buttons) that can't carry the styled attrs. */
export const RIDE_INVESTIGATION_E2E = {
  triggerButton: "ride-investigation-trigger",
  playToggle: "ride-investigation-play-toggle",
  followToggle: "ride-investigation-follow-toggle",
  askAiSend: "ride-investigation-ask-ai-send",
  historyRefresh: "ride-investigation-history-refresh",
} as const;

/** History tab — thresholds for condensing the drive, and the tab's fixed orderings. */
export const INVESTIGATION_HISTORY = {
  i18nKey: "rideMap.investigation.history",
  /** A row is added the first time the delay reaches each of these (minutes); from `critical` on it is critical. */
  delayThresholdMinutes: { warning: 5, late: 10, critical: 15 },
  /** A stop reached within this many minutes of its schedule counts as on time. */
  onTimeToleranceMinutes: 5,
  /** A start within this many seconds of the schedule counts as on time. */
  startOnTimeToleranceSec: 60,
  /** Rides are closed by an hourly job, so an arrived ride counts as never closed only after this long. */
  closeGraceMs: 7_200_000,
  /** GPS still arriving this long (seconds) after the last stop is worth a row. */
  gpsAfterEndMinSec: 120,
  filters: [
    "all",
    "drive",
    "changes",
    "supplier",
    "bulk",
  ] satisfies readonly TInvestigationHistoryFilter[],
  lifecycleSteps: [
    "created",
    "sent",
    "started",
    "arrived",
    "closed",
  ] satisfies readonly TInvestigationLifecycleStepKey[],
  /** Statuses after which a ride is finished, whether it was driven or not. */
  closedStatuses: [
    RideStatusEnum.Done,
    RideStatusEnum.DoneApproved,
    RideStatusEnum.Canceled,
    RideStatusEnum.Rejected,
    RideStatusEnum.Declined,
  ],
  /** Closing statuses of a ride that was never meant to be driven. */
  notDrivenStatuses: [
    RideStatusEnum.Canceled,
    RideStatusEnum.Rejected,
    RideStatusEnum.Declined,
  ],
  /** Steps a ride closed without being driven may legitimately never reach (cancelled before it was sent). */
  notDrivenOptionalSteps: [
    "sent",
    "started",
    "arrived",
  ] satisfies readonly TInvestigationLifecycleStepKey[],
  /**
   * Statuses a ride reaches once its dispatch went through: Sent, or Received when the supplier confirms at once (Gett
   * maps its order states straight to it). Neither is set when a supplier rejects the booking.
   */
  dispatchedStatuses: [RideStatusEnum.Sent, RideStatusEnum.Received],
  /** Steps only the audit trail can prove (the dispatch); unknown rather than missing when it is incomplete. */
  auditOnlySteps: ["sent"] satisfies readonly TInvestigationLifecycleStepKey[],
  /** Passenger names listed on a condensed boarding / no-show row before it says how many more. */
  maxNamesShown: 5,
  /** Investigation events repeated in the history one by one (passenger events are condensed per stop instead). */
  driveEventTypes: [
    RideInvestigationEventTypeEnum.GpsGap,
    RideInvestigationEventTypeEnum.DetourEntered,
    RideInvestigationEventTypeEnum.UnexpectedStop,
    RideInvestigationEventTypeEnum.Incident,
  ],
  /** Ride history drawer columns left out of a change's summary: the passenger rows already name who was added or removed. */
  auditSkippedColumns: new Set<string>(["passengerNames"]),
} as const;

/** Passenger events the history condenses per stop, and the drive row each becomes. */
export const INVESTIGATION_HISTORY_PASSENGER_EVENT_KIND: Partial<
  Record<RideInvestigationEventTypeEnum, TInvestigationDriveRowKind>
> = {
  [RideInvestigationEventTypeEnum.PassengerBoarded]: "boarded",
  [RideInvestigationEventTypeEnum.PassengerNoShow]: "noShow",
};

/** Which History filter each backend source belongs to. */
export const INVESTIGATION_HISTORY_SOURCE_GROUP: Record<
  RideInvestigationHistorySourceEnum,
  TInvestigationHistoryGroup
> = {
  [RideInvestigationHistorySourceEnum.Lifecycle]: "changes",
  [RideInvestigationHistorySourceEnum.Audit]: "changes",
  [RideInvestigationHistorySourceEnum.Passenger]: "changes",
  [RideInvestigationHistorySourceEnum.Supplier]: "supplier",
  [RideInvestigationHistorySourceEnum.Process]: "bulk",
};

const INVESTIGATION_DRIVE_ROW_ICON: Record<
  TInvestigationDriveRowKind,
  SvgIconComponent
> = {
  started: PlayArrow,
  onTime: CheckCircleOutlined,
  delay: Schedule,
  peakDelay: TrendingUp,
  noArrival: LocationOff,
  outOfOrder: SwapVert,
  arrived: Flag,
  gpsAfterEnd: SatelliteAlt,
  passedWithoutStopping: FastForward,
  boarded: PersonAddAlt,
  noShow: PersonOff,
};

const INVESTIGATION_HISTORY_ENTRY_ICON: Record<
  RideInvestigationHistoryEntryTypeEnum,
  SvgIconComponent
> = {
  [RideInvestigationHistoryEntryTypeEnum.RideCreated]: AddCircleOutlined,
  [RideInvestigationHistoryEntryTypeEnum.RecordCreated]: AddCircleOutlined,
  [RideInvestigationHistoryEntryTypeEnum.RecordUpdated]: EditOutlined,
  [RideInvestigationHistoryEntryTypeEnum.RecordDeleted]: Inventory2Outlined,
  [RideInvestigationHistoryEntryTypeEnum.StatusChanged]: SyncAlt,
  [RideInvestigationHistoryEntryTypeEnum.BulkAction]: DynamicFeedOutlined,
  [RideInvestigationHistoryEntryTypeEnum.SupplierCreate]: LocalShippingOutlined,
  [RideInvestigationHistoryEntryTypeEnum.SupplierUpdate]: SendOutlined,
  [RideInvestigationHistoryEntryTypeEnum.SupplierCancel]: CancelScheduleSend,
  [RideInvestigationHistoryEntryTypeEnum.SupplierPolling]: Sync,
  [RideInvestigationHistoryEntryTypeEnum.SupplierAuthorize]: VpnKeyOutlined,
  [RideInvestigationHistoryEntryTypeEnum.SupplierCall]: ManageSearch,
  [RideInvestigationHistoryEntryTypeEnum.SupplierFailed]: ErrorOutlineOutlined,
  [RideInvestigationHistoryEntryTypeEnum.PassengerAdded]: PersonAddAlt,
  [RideInvestigationHistoryEntryTypeEnum.PassengerRemoved]:
    PersonRemoveOutlined,
};

/** Icon per History row — backend entry types, repeated investigation events and drive moments. */
export const INVESTIGATION_HISTORY_ICON: Record<
  TInvestigationHistoryIconKey,
  SvgIconComponent
> = {
  ...RIDE_INVESTIGATION_EVENT_ICON,
  ...INVESTIGATION_HISTORY_ENTRY_ICON,
  ...INVESTIGATION_DRIVE_ROW_ICON,
};
